/**
 * Turning what the AI heard on a call into what the service-call form holds.
 *
 * The two disagree on shape, and the gap is where an autofill quietly goes
 * wrong. The intake keeps a customer's words — one line of address, "same day",
 * "tomorrow before noon". The form keeps structure — street/city/state/zip, a
 * three-value urgency, a date range it will search technician availability
 * against. Everything below is the honest half of that translation: what maps
 * cleanly maps, what does not is carried into a note where a person can read it
 * rather than being guessed into a field the form will act on.
 *
 * Pure and separate from both, so the mapping can be checked directly — it is
 * the part with the sharp edges, not the button that calls it.
 */

import type { CallIntake, CallIntakeFields, IntakeCustomFieldAnswer } from '@/lib/calls/intakeApi';

/** The form's address fields, as `AppointmentAddressFields` holds them. */
export interface ParsedAddress {
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}

/** The form's three-level urgency. */
export type FormUrgency = 'standard' | 'urgent' | 'emergency';

const STATE_CODES = new Set([
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
  'PR',
  // Canadian provinces and territories — the same field, the same position in a
  // spoken address, and a customer in Vancouver is not an edge case.
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
]);

const STATE_NAMES: Record<string, string> = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  alberta: 'AB',
  'british columbia': 'BC',
  manitoba: 'MB',
  'new brunswick': 'NB',
  'nova scotia': 'NS',
  ontario: 'ON',
  quebec: 'QC',
  saskatchewan: 'SK',
};

/** US ZIP, or a Canadian postal code in any of the ways it gets written down. */
const US_ZIP = /\b(\d{5}(?:-\d{4})?)\s*$/;
const CA_POSTAL = /\b([A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)\s*$/;

/** A city name never contains a digit — a fragment that does is not one. */
function looksLikeCity(part: string): boolean {
  return part.length > 0 && !/\d/.test(part);
}

/**
 * Words a caller says AROUND the value rather than as part of it.
 *
 * The transcript carries "the zip code is 59715" verbatim and the label rode
 * into the field with the number. Only labels are stripped, and only as whole
 * words: "State Street" is a street, and a rule that ate "state" would quietly
 * rename it.
 */
function stripLabels(line: string): string {
  return line
    .replace(/\b(?:zip|postal)\s*code\b/gi, ' ')
    .replace(/\b(?:the\s+)?(?:address|street\s+address)\s+is\b/gi, ' ')
    .replace(/\s*,\s*/g, ', ')
    .replace(/[ \t]+/g, ' ')
    .replace(/^[,\s]+|[,\s]+$/g, '')
    .trim();
}

/** A trailing country, dropped — the form keeps country in its own field. */
function stripCountry(line: string): string {
  return line
    .replace(/,?\s*(canada|u\.?s\.?a\.?|united states(?: of america)?)\s*$/i, '')
    .trim()
    .replace(/[,\s]+$/, '');
}

/**
 * A spoken address, split into the fields the form books against.
 *
 * Conservative, and conservative in the direction that matters: a fragment
 * holding a digit is never written into City. That was the bug worth fixing —
 * "5600 BC Canada, ZIP code, V35143" carries no US ZIP, so the old rule took the
 * last comma-part as the city and booked a visit in a town called V35143.
 *
 * With no comma to go on the whole line stays in `street` rather than being cut
 * at a guessed boundary: a wrong city sends a technician to another town, while
 * a long street line is visibly wrong and takes one edit to fix.
 */
export function parseSpokenAddress(line: string | null | undefined): ParsedAddress | null {
  const raw = line?.trim();
  if (!raw) return null;

  let rest = stripCountry(stripLabels(raw));
  let zip = '';
  let state = '';

  const postal = rest.match(CA_POSTAL) ?? rest.match(US_ZIP);
  if (postal) {
    zip = postal[1].toUpperCase();
    rest = rest
      .slice(0, postal.index)
      .trim()
      .replace(/[,\s]+$/, '');
  }

  const codeMatch = rest.match(/[,\s]+([A-Za-z]{2})\s*$/);
  const nameMatch = rest.match(/[,\s]+([A-Za-z]+(?:\s+[A-Za-z]+)?)\s*$/);
  if (codeMatch && STATE_CODES.has(codeMatch[1].toUpperCase())) {
    state = codeMatch[1].toUpperCase();
    rest = rest
      .slice(0, codeMatch.index)
      .trim()
      .replace(/[,\s]+$/, '');
  } else if (nameMatch && STATE_NAMES[nameMatch[1].toLowerCase()]) {
    state = STATE_NAMES[nameMatch[1].toLowerCase()];
    rest = rest
      .slice(0, nameMatch.index)
      .trim()
      .replace(/[,\s]+$/, '');
  }

  const parts = rest
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  // A trailing one-word fragment with a digit in it is a postcode we did not
  // recognise — a mis-transcribed one, most often — and never a city. Take it as
  // the postcode when we have none; keep it out of City either way.
  const last = parts[parts.length - 1];
  if (parts.length > 1 && last && !looksLikeCity(last) && !/\s/.test(last)) {
    parts.pop();
    if (!zip) zip = last.toUpperCase();
  }

  // One fragment left. Which field it is depends on what it looks like, not on
  // its position: a house number means a street, and a bare name after a state
  // or postcode has already been lifted off is the town.
  //
  // "Vancouver, BC, Canada" — a job described by town, with no number yet —
  // used to put Vancouver in Street and leave City empty, which is not an
  // address the availability search can use.
  if (parts.length <= 1) {
    const only = parts[0] ?? '';
    const isTown = only !== '' && !/\d/.test(only) && (Boolean(state) || Boolean(zip));
    if (isTown) return { street: '', street2: '', city: only, state, zip };
    return { street: only, street2: '', city: '', state, zip };
  }

  const city = looksLikeCity(parts[parts.length - 1]) ? (parts.pop() as string) : '';
  const street = parts.shift() ?? '';
  return { street, street2: parts.join(', '), city, state, zip };
}

/**
 * The address to put in the form, from the best source available.
 *
 * The extractor now returns the split alongside the line, done by the model
 * that had the transcript — which beats anything derivable here, because the
 * words it split were "the zip code is five four two one two" rather than a
 * comma-separated string. `parseSpokenAddress` stays as the fallback for an
 * intake extracted before that shipped, and for the day the model returns the
 * line without the parts.
 */
export function addressFieldsFrom(intake: CallIntake | null | undefined): ParsedAddress | null {
  const parts = intake?.addressParts;
  const fromModel =
    parts &&
    (parts.street || parts.unit || parts.city || parts.state || parts.postalCode) &&
    ({
      street: parts.street?.trim() ?? '',
      street2: parts.unit?.trim() ?? '',
      city: parts.city?.trim() ?? '',
      state: parts.state?.trim() ?? '',
      zip: parts.postalCode?.trim() ?? '',
    } satisfies ParsedAddress);
  return fromModel || parseSpokenAddress(intake?.fields.address);
}

/**
 * The customer's sense of "when" against the form's three levels.
 *
 * `next_few_days` and `flexible` both land on standard: the form's middle level
 * is "urgent", and a customer who is happy to wait until Thursday is not that.
 */
export function mapUrgency(urgency: string | null | undefined): FormUrgency | null {
  switch (urgency) {
    case 'emergency':
      return 'emergency';
    case 'same_day':
      return 'urgent';
    case 'next_few_days':
    case 'flexible':
      return 'standard';
    default:
      return null;
  }
}

/**
 * How soon they need someone, as the priority a JOB carries.
 *
 * A job's scale is LOW | NORMAL | HIGH | URGENT, which is not the visit
 * form's three levels — the same customer sentence maps to different values on
 * the two records, and reading one scale into the other is how an emergency
 * quietly becomes routine.
 */
export function jobPriorityFromUrgency(
  urgency: string | null | undefined,
): 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT' | null {
  switch (urgency) {
    case 'emergency':
      // URGENT, not EMERGENCY: the job form's own dropdown produces URGENT and
      // labels it "Emergency" (job TYPES use EMERGENCY — see jobFormModel).
      // An autofill has to write what a person picking from that list writes.
      return 'URGENT';
    case 'same_day':
      return 'HIGH';
    case 'next_few_days':
    case 'flexible':
      return 'NORMAL';
    default:
      return null;
  }
}

/**
 * The things the form has no field for, written out for whoever reads the job.
 *
 * Preferred time is the important one: "tomorrow before noon" cannot be turned
 * into a date range without guessing a date, and a guessed date is a booked
 * visit at the wrong time. It goes in the notes, in the customer's words, where
 * the dispatcher scheduling this will actually see it.
 */
export function noteLinesFrom(fields: Partial<CallIntakeFields>): string[] {
  const lines: string[] = [];
  if (fields.preferredTime?.trim()) lines.push(`Preferred time: ${fields.preferredTime.trim()}`);
  if (fields.accessNotes?.trim()) lines.push(`Access: ${fields.accessNotes.trim()}`);
  if (fields.poNumber?.trim()) lines.push(`PO number: ${fields.poNumber.trim()}`);
  return lines;
}

/** One thing the autofill can put somewhere, and where it would go. */
export interface AutofillItem {
  /** The intake field this came from — the key the card labels it by. */
  field: keyof CallIntakeFields | 'customerPhone' | `customField:${string}`;
  /** For a custom field, its own label — the tenant named it, not us. */
  label?: string;
  /** What the agent sees offered. */
  display: string;
  /**
   * Where it lands, for the "goes to notes" cases the card should be honest
   * about.
   *
   * `notes` is the only target that is always safe to fill: notes are APPENDED
   * to whatever is there, so a fill can never destroy something the agent
   * typed. Every other target overwrites a single field and must be
   * occupancy-checked first. PO number has its own target for exactly that
   * reason — it goes to the job's PO field, not to notes, so bucketing it with
   * the append-only ones let a blind fill overwrite a PO the agent had already
   * entered, and told them it was going somewhere it wasn't.
   */
  target:
    | 'customer'
    | 'address'
    | 'jobType'
    | 'issue'
    | 'urgency'
    | 'poNumber'
    | 'notes'
    /** One of the tenant's own job fields, filled by its own key. */
    | 'customField';
}

/**
 * Everything this intake could fill, in the order the form reads top to bottom.
 *
 * Nothing here decides whether to APPLY it — the card compares against what the
 * form already holds and the agent decides. This only answers "what is there".
 */
/** The form key an autofill item writes to, for a custom field. */
export function customFieldKeyOf(item: AutofillItem): string | null {
  return item.target === 'customField' && typeof item.field === 'string'
    ? item.field.slice('customField:'.length) || null
    : null;
}

/**
 * What a custom-field answer looks like IN THE FORM, which is not what it looks
 * like on the card.
 *
 * Three different strings are wanted from one answer and the difference between
 * them is the difference between a saved job and a rejected one:
 *
 *   - A DROPDOWN stores the option's KEY. The customer said "Gas", the tenant
 *     configured `{ key: 'gas', label: 'Gas' }`, and a form holding "Gas"
 *     renders an empty select and fails validation on save.
 *   - A MULTI_SELECT stores a JSON array of keys — the draft encoding the
 *     multi-select input reads and writes.
 *   - A TEXT field stores the words, and only there is the answer the display.
 *
 * Returns '' for anything it cannot turn into a value the form would accept,
 * because an autofill that writes something unsaveable is worse than one that
 * writes nothing.
 */
export function customFieldDraftValue(answer: IntakeCustomFieldAnswer): string {
  const keys = (answer.optionKeys ?? []).map((key) => key.trim()).filter(Boolean);
  switch (answer.type) {
    case 'MULTI_SELECT':
      return keys.length ? JSON.stringify(keys) : '';
    case 'DROPDOWN':
      // One key, even if the model offered several: a dropdown holds one value,
      // and picking the first of an ambiguous set is the guess this avoids.
      return keys.length === 1 ? keys[0] : '';
    case 'TEXT':
      return answer.text?.trim() ?? '';
    default:
      // No type at all is an ai-operations older than the three-way split,
      // which sent one `value` for every field. Read it as it was written.
      return answer.value?.trim() ?? answer.text?.trim() ?? '';
  }
}

/** The answer as a PERSON reads it — labels, never keys. */
export function customFieldDisplayValue(answer: IntakeCustomFieldAnswer): string {
  return (answer.display?.trim() || answer.text?.trim() || answer.value?.trim()) ?? '';
}

export function autofillItems(intake: CallIntake | null | undefined): AutofillItem[] {
  if (!intake) return [];
  const f = intake.fields;
  const items: AutofillItem[] = [];

  if (f.customerName?.trim())
    items.push({ field: 'customerName', display: f.customerName.trim(), target: 'customer' });
  if (intake.customerPhone?.trim())
    items.push({
      field: 'customerPhone',
      display: intake.customerPhone.trim(),
      target: 'customer',
    });
  if (f.email?.trim()) items.push({ field: 'email', display: f.email.trim(), target: 'customer' });
  if (f.address?.trim())
    items.push({ field: 'address', display: f.address.trim(), target: 'address' });
  if (f.jobTypeName?.trim())
    items.push({ field: 'jobTypeName', display: f.jobTypeName.trim(), target: 'jobType' });
  if (f.issueSummary?.trim())
    items.push({ field: 'issueSummary', display: f.issueSummary.trim(), target: 'issue' });
  if (f.urgency && mapUrgency(f.urgency))
    items.push({
      field: 'urgency',
      display: mapUrgency(f.urgency) as string,
      target: 'urgency',
    });
  if (f.preferredTime?.trim())
    items.push({ field: 'preferredTime', display: f.preferredTime.trim(), target: 'notes' });
  if (f.accessNotes?.trim())
    items.push({ field: 'accessNotes', display: f.accessNotes.trim(), target: 'notes' });
  if (f.poNumber?.trim())
    items.push({ field: 'poNumber', display: f.poNumber.trim(), target: 'poNumber' });

  // The tenant's own job fields, last: they belong to the job type, so they
  // only exist once the call has one, and they read below the fields every job
  // has. A field with no key cannot be written to the form, so it is not
  // offered — showing a value the button could not apply is worse than
  // showing nothing.
  for (const answer of intake.customFields ?? []) {
    // A field with no key cannot be written to the form, and neither can an
    // answer that does not resolve to a value the form would accept — a
    // dropdown answered with something outside its option list, say. Offering
    // either shows the agent a row whose button does nothing.
    if (!answer.fieldKey || !customFieldDraftValue(answer)) continue;
    items.push({
      field: `customField:${answer.fieldKey}`,
      label: answer.label,
      display: customFieldDisplayValue(answer) || customFieldDraftValue(answer),
      target: 'customField',
    });
  }

  return items;
}

/**
 * Append note lines that are not already in the note.
 *
 * Autofill is pressable twice — a call goes on, the AI hears more, the agent
 * fills again — and the second press must not stack a second copy of "Access:
 * gate code 1234" under the first.
 */
/**
 * May Iris still write to this control, or has the operator taken it?
 *
 * The same rule the field marks run on, in one line: a control still holding
 * exactly what she last put there is hers to correct, and anything else is
 * theirs to keep. The value IS the flag — there is no dirty bit to keep in
 * step, and an operator who picks Morning by hand is told apart from an Iris
 * who picked it by what was written last, not by who typed.
 *
 * `wrote` is null before she has written at all, which is why `resting` is
 * needed: a control still sitting at the value it opened on is unclaimed, and
 * a control the operator moved before she ever spoke is already theirs.
 */
export function irisMayWrite<T>(current: T, wrote: T | null, resting: T): boolean {
  return current === (wrote ?? resting);
}

/**
 * Is this exact line still in the notes, untouched?
 *
 * The ownership test for a note-backed field, and it has to be the SAME test
 * `withoutNoteLine` removes by. A looser `includes` said an agent-edited line
 * — "Preferred time: tomorrow morning, call first" — was still hers, so a
 * later change removed nothing and appended a second preferred-time line
 * underneath, leaving the technician two instructions that disagree.
 */
export function hasExactNoteLine(existing: string, line: string | null): boolean {
  if (!line?.trim()) return false;
  return existing.split('\n').some((existingLine) => existingLine.trim() === line.trim());
}

/**
 * Drop one line Iris wrote earlier, so a newer one replaces it rather than
 * stacking under it.
 *
 * A caller who says "tomorrow" and then "actually Friday" was leaving both in
 * the notes — "Preferred time: tomorrow" above "Preferred time: Friday" — and
 * the technician got to choose which to believe. Matches the whole line, so a
 * line the agent typed themselves is never what goes.
 */
export function withoutNoteLine(existing: string, line: string | null): string {
  if (!line?.trim()) return existing;
  return existing
    .split('\n')
    .filter((existingLine) => existingLine.trim() !== line.trim())
    .join('\n');
}

export function mergeNotes(existing: string, lines: string[]): string {
  const current = existing.trim();
  const fresh = lines.filter((line) => !current.includes(line));
  if (!fresh.length) return existing;
  return current ? `${current}\n${fresh.join('\n')}` : fresh.join('\n');
}
